import { supabase } from '../lib/supabase';

/** Unified per-user pool size (Jobs tab + Agent recommended feed). */
export const RECOMMENDED_POOL_LIMIT = 300;

const PEERLIST_ID_CHUNK = 80;

/** Fields needed by AgentJobsPanel cards. */
export const PEERLIST_JOB_AGENT_SELECT =
  'id, title, company, company_logo_url, location, remote, job_type, experience_level, ' +
  'skills, must_have_skills, good_to_have_skills, url, salary_min, salary_max, salary_currency, ' +
  'compensation_text, posted_at, description_md, source_channel';

export type PeerlistAgentJobRow = {
  id: string;
  title: string;
  company: string;
  company_logo_url?: string | null;
  location?: string | null;
  remote?: boolean | null;
  job_type?: string | null;
  experience_level?: string | null;
  skills?: string[] | null;
  must_have_skills?: string[] | null;
  good_to_have_skills?: string[] | null;
  url?: string | null;
  salary_min?: number | null;
  salary_max?: number | null;
  salary_currency?: string | null;
  compensation_text?: string | null;
  posted_at?: string | null;
  description_md?: string | null;
  source_channel?: string | null;
};

export type RecommendedJobSource = 'internal' | 'external' | 'cron' | 'ai';

export interface RecommendedJobRow {
  job_id: string;
  source: RecommendedJobSource;
  fetch_batch_id: string | null;
  added_at: string;
}

/** Chunked peerlist_jobs load by id (preserves recommended_jobs order). */
export async function fetchPeerlistAgentJobsByIds(
  jobIds: string[],
): Promise<PeerlistAgentJobRow[]> {
  if (jobIds.length === 0) return [];

  const byId = new Map<string, PeerlistAgentJobRow>();
  for (let i = 0; i < jobIds.length; i += PEERLIST_ID_CHUNK) {
    const chunk = jobIds.slice(i, i + PEERLIST_ID_CHUNK);
    const { data, error } = await supabase
      .from('peerlist_jobs')
      .select(PEERLIST_JOB_AGENT_SELECT)
      .in('id', chunk)
      .eq('is_active', true);

    if (error) {
      console.warn('[recommendedJobs] peerlist_jobs by id failed:', error.message);
      continue;
    }
    for (const row of (data ?? []) as PeerlistAgentJobRow[]) {
      byId.set(row.id, row);
    }
  }

  return jobIds
    .map((id) => byId.get(id))
    .filter((j): j is PeerlistAgentJobRow => j != null);
}

/** Sync + list + hydrate — same pipeline as Jobs tab recommended feed. */
export async function loadUnifiedRecommendedAgentJobs(
  limit = RECOMMENDED_POOL_LIMIT,
): Promise<{
  jobs: PeerlistAgentJobRow[];
  recRows: RecommendedJobRow[];
  syncResult: Awaited<ReturnType<typeof syncRecommendedJobs>>;
}> {
  const syncResult = await syncRecommendedJobs(limit);
  const recRows = await listRecommendedJobs(limit);
  const jobIds = recRows.map((r) => r.job_id).filter(Boolean);
  const jobs = await fetchPeerlistAgentJobsByIds(jobIds);
  return { jobs, recRows, syncResult };
}

/** Populate unified recommended_jobs from all ingest channels + AI matches. */
export async function syncRecommendedJobs(limit = RECOMMENDED_POOL_LIMIT): Promise<{
  ai_added?: number;
  cron_added?: number;
  catalog_added?: number;
  total_for_user?: number;
} | null> {
  const { data, error } = await supabase.rpc('sync_recommended_jobs_internal', {
    p_limit: limit,
  });
  if (error) {
    console.warn('[recommendedJobs] sync failed:', error.message);
    return null;
  }
  return (data ?? null) as {
    ai_added?: number;
    cron_added?: number;
    catalog_added?: number;
    total_for_user?: number;
  } | null;
}

/** @deprecated use syncRecommendedJobs */
export const syncRecommendedJobsInternal = syncRecommendedJobs;

/** Unified feed — all channels from recommended_jobs (no internal/external split). */
export async function listRecommendedJobs(limit = RECOMMENDED_POOL_LIMIT): Promise<RecommendedJobRow[]> {
  const { data, error } = await supabase.rpc('get_recommended_job_ids', {
    p_mode: 'all',
    p_fetch_batch_id: null,
    p_limit: limit,
  });

  if (!error) {
    return (data ?? []) as RecommendedJobRow[];
  }

  console.warn('[recommendedJobs] RPC list failed, trying table:', error.message);

  const { data: rows, error: tableErr } = await supabase
    .from('recommended_jobs')
    .select('job_id, source, fetch_batch_id, added_at')
    .order('added_at', { ascending: false })
    .limit(limit);

  if (tableErr) {
    console.warn('[recommendedJobs] table read failed:', tableErr.message);
    return [];
  }

  return (rows ?? []) as RecommendedJobRow[];
}

/** @deprecated use listRecommendedJobs */
export async function listRecommendedJobIds(
  _mode?: 'internal' | 'external',
  _fetchBatchId?: string | null,
  limit = 300,
): Promise<RecommendedJobRow[]> {
  return listRecommendedJobs(limit);
}

/** @deprecated use listRecommendedJobs */
export async function listInternalRecommendedJobIds(limit = 300): Promise<RecommendedJobRow[]> {
  return listRecommendedJobs(limit);
}
